import {useEffect, useMemo, useState} from "react";
import {PhoneHQGlyph, PhoneHQAvatar, PhoneHQButton, PhoneHQInput} from 'ch3-ui-lib';
import {useForm, Controller, useFieldArray, type SubmitHandler} from "react-hook-form";
import {isValidPhoneNumber} from "libphonenumber-js/max";
import {validateEmail} from "../../utilis/commonValidators";
import {PhoneHQPhoneNumberInput} from "../../components/Dialer/components/PhoneHQPhoneNumberInput";
import {
    type ContactDTO,
    useAddContactMutation,
    useGetContactByIdQuery,
    useUpdateContactMutation,
} from "../../store/features/Contacts/contactsService";
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {Info, X, Plus} from "lucide-react";
import {AccessSelector, type AccessMode, type SelectedUser, type SelectedGroup} from "./AccessSelector";
import {getPrivateAvailabilityRestriction} from "./contactAccessPolicy";
import 'react-international-phone/style.css';

interface ContactPanelProps {
    open: boolean;
    onClose: () => void;
    contactId?: number;
    initialPhoneNumber?: string;
}

interface ContactFormValues {
    firstName: string;
    lastName: string;
    phoneNumbers: {msisdn: string | number; main: boolean}[];
    email: string;
    company: string;
    availability: AccessMode;
}

export default function ContactPanel({open, onClose, contactId, initialPhoneNumber}: ContactPanelProps) {
    const isEdit = contactId !== undefined && contactId !== null;
    const userId = useSelector(selectUserId);
    const [createContact] = useAddContactMutation();
    const [updateContact] = useUpdateContactMutation();
    const {data: contactData} = useGetContactByIdQuery(contactId as number, {
        skip: !open || !isEdit,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Sharing state — the members/groups search itself lives in <AccessSelector/>.
    const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>([]);

    const {
        control,
        handleSubmit,
        watch,
        formState: {errors},
        reset,
    } = useForm<ContactFormValues>({
        mode: "onBlur",
        defaultValues: {
            firstName: "",
            lastName: "",
            phoneNumbers: [{msisdn: "", main: true}],
            email: "",
            company: "",
            availability: "PRIVATE" as AccessMode,
        },
    });

    const {fields, append, remove} = useFieldArray({control, name: "phoneNumbers"});

    useEffect(() => {
        if (isEdit || !open) return;

        reset({
            firstName: "",
            lastName: "",
            phoneNumbers: [{msisdn: initialPhoneNumber ?? "", main: true}],
            email: "",
            company: "",
            availability: "PRIVATE",
        });
        setSelectedUsers([]);
        setSelectedGroups([]);
    }, [initialPhoneNumber, isEdit, open, reset]);

    // Hydrate form from existing contact in edit mode
    useEffect(() => {
        if (!isEdit) return;
        if (contactData && open) {
            reset({
                firstName: contactData.firstName || "",
                lastName: contactData.lastName || "",
                phoneNumbers: contactData.phoneNumbers?.length
                    ? contactData.phoneNumbers.map(phoneNumber => ({
                        msisdn: String(phoneNumber.msisdn),
                        main: phoneNumber.main,
                    }))
                    : [{msisdn: "", main: true}],
                email: contactData.email || "",
                company: contactData.company || "",
                availability: (contactData.availability as AccessMode) || "PUBLIC",
            });
            // Only pre-fill the shared list for an actually-SHARED contact. A PUBLIC
            // contact reports every user/group as having access, which would otherwise
            // flood the list the moment the user switches to SHARED.
            const isShared = contactData.availability === "SHARED";
            setSelectedUsers(isShared ? (contactData.sharedForUsers ?? []).map(user => ({
                userId: user.userId,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarUrl: user.avatarUrl,
            })) : []);
            setSelectedGroups(isShared ? (contactData.sharedForGroups ?? []).flatMap(group =>
                group.groupId === undefined ? [] : [{
                    groupId: group.groupId,
                    name: group.name,
                    icon: group.icon,
                    iconColor: group.iconColor,
                }]
            ) : []);
        }
    }, [contactData, open, reset, isEdit]);

    const firstName = watch("firstName");
    const lastName = watch("lastName");
    const availability = watch("availability");
    const privateRestriction = useMemo(
        () => isEdit && contactData
            ? getPrivateAvailabilityRestriction(userId, [contactData])
            : undefined,
        [contactData, isEdit, userId],
    );
    const privateAvailabilityNeedsChanging = privateRestriction !== undefined && availability === "PRIVATE";

    const initials = useMemo(() => {
        const f = firstName?.trim()?.[0] ?? "";
        const l = lastName?.trim()?.[0] ?? "";
        return (f + l).toUpperCase() || "";
    }, [firstName, lastName]);

    const handleClose = () => {
        if (!isEdit) {
            reset();
            setSelectedUsers([]);
            setSelectedGroups([]);
        }
        onClose();
    };

    const onSubmit: SubmitHandler<ContactFormValues> = async (data) => {
        try {
            setIsSubmitting(true);
            const payload: ContactDTO = {
                contactId: contactData?.contactId,
                firstName: data.firstName || undefined,
                lastName: data.lastName || undefined,
                email: data.email || undefined,
                company: data.company || undefined,
                availability: data.availability,
                sharedForUsers: selectedUsers.map(user => user.userId),
                sharedForGroups: selectedGroups.map(group => group.groupId),
                phoneNumbers: data.phoneNumbers.map(phoneNumber => ({
                    msisdn: Number(phoneNumber.msisdn),
                    main: phoneNumber.main,
                })),
            };

            if (isEdit) {
                await updateContact(payload).unwrap();
            } else {
                await createContact(payload).unwrap();
            }
            handleClose();
        } catch (error) {
            console.error(`Error ${isEdit ? "updating" : "creating"} contact:`, error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!open) return null;

    const title = isEdit ? "Edit contact" : "Create contact";
    const submitLabel = isEdit ? "Save changes" : "Create";

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-scrim" onClick={handleClose}/>

            <div role="dialog" aria-label={typeof title === "string" ? title : undefined} className="relative flex w-[640px] max-w-[90vw] flex-col overflow-hidden bg-surface shadow-overlay">
                {/* Header */}
                <div className="flex h-header shrink-0 items-center justify-between border-0 border-b border-solid border-line pl-5 pr-3">
                    <h2 className="m-0 text-body-strong text-ink">{title}</h2>
                    <button
                        onClick={handleClose}
                        className="flex size-8 cursor-pointer items-center justify-center rounded-chip text-ink-secondary hover:bg-surface-muted"
                    >
                        <X size={18} className="!p-0 !text-current"/>
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto">
                    {/* Profile */}
                    <div className="border-0 border-b border-solid border-line p-5">
                        <div className="flex gap-4">
                            <div className="shrink-0">
                                {initials ? (
                                    <PhoneHQAvatar
                                        size="64px"
                                        type="initials"
                                        name={`${firstName} ${lastName}`}
                                        colorFromName
                                    />
                                ) : (
                                    <div className="flex size-14 items-center justify-center rounded-card bg-surface-muted text-ink-muted">
                                        <PhoneHQGlyph name="account_circle" size={32} />
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-1 flex-col gap-4">
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <Controller
                                            name="firstName"
                                            control={control}
                                            rules={{required: "First name is required"}}
                                            render={({field}) => (
                                                <PhoneHQInput
                                                    label="First name"
                                                    className="w-full h-[36px]"
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    onBlur={field.onBlur}
                                                    error={errors.firstName?.message}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <Controller
                                            name="lastName"
                                            control={control}
                                            render={({field}) => (
                                                <PhoneHQInput
                                                    label="Last name"
                                                    className="w-full h-[36px]"
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    onBlur={field.onBlur}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Phones */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-footnote font-medium text-ink-secondary">Phone number</label>
                                    {/* Cap the list height so many numbers scroll here instead of pushing
                                        the Access section (and its dropdown) out of view. */}
                                    <div className="flex flex-col gap-2 max-h-[168px] overflow-y-auto">
                                    {fields.map((field, index) => (
                                        <div className="flex items-center gap-2" key={field.id}>
                                            <div className="flex-1">
                                                <Controller
                                                    name={`phoneNumbers.${index}.msisdn`}
                                                    control={control}
                                                    rules={{
                                                        required: "Phone number is required",
                                                        validate: {
                                                            isValid: (value: string | number) =>
                                                                isValidPhoneNumber(`+${value}`) || "Phone number is not valid",
                                                        },
                                                    }}
                                                    render={({field: phoneField}) => (
                                                        <PhoneHQPhoneNumberInput
                                                            phoneNumber={String(phoneField.value)}
                                                            onPhoneNumberChange={(value: number) => phoneField.onChange(value)}
                                                            error={errors.phoneNumbers?.[index]?.msisdn?.message}
                                                            onBlur={phoneField.onBlur}
                                                            name={phoneField.name}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            {fields.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => remove(index)}
                                                    className="flex size-7 cursor-pointer items-center justify-center rounded-chip text-ink-muted hover:bg-surface-muted"
                                                >
                                                    <PhoneHQGlyph name="delete" size={12}/>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    </div>

                                    <div>
                                        <PhoneHQButton
                                            variant="tertiary"
                                            size="sm"
                                            onClick={() => append({msisdn: "", main: false})}
                                        >
                                            <Plus size={18} className={'!p-0'} />
                                            <span>Add another number</span>

                                        </PhoneHQButton>
                                    </div>
                                </div>

                                {/* Email + Company */}
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <Controller
                                            name="email"
                                            control={control}
                                            rules={{
                                                validate: {
                                                    isValid: (value: string) =>
                                                        value ? validateEmail(value) || "Invalid email format" : true,
                                                },
                                            }}
                                            render={({field}) => (
                                                <PhoneHQInput
                                                    label="E-mail"
                                                    className="w-full h-[36px]"
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    onBlur={field.onBlur}
                                                    error={errors.email?.message}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <Controller
                                            name="company"
                                            control={control}
                                            render={({field}) => (
                                                <PhoneHQInput
                                                    label="Company"
                                                    className="w-full h-[36px]"
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    onBlur={field.onBlur}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Access */}
                    <div className="p-5">
                        <h3 className="m-0 mb-2.5 text-callout font-semibold text-ink">Access</h3>

                        <Controller
                            name="availability"
                            control={control}
                            render={({field}) => (
                                <AccessSelector
                                    value={field.value}
                                    onChange={field.onChange}
                                    selectedUsers={selectedUsers}
                                    selectedGroups={selectedGroups}
                                    onSelectedUsersChange={setSelectedUsers}
                                    onSelectedGroupsChange={setSelectedGroups}
                                    active={open}
                                    disabledOptions={privateRestriction ? {PRIVATE: privateRestriction.reason} : undefined}
                                />
                            )}
                        />
                        {privateAvailabilityNeedsChanging && (
                            <div className="mt-3 flex items-start gap-2 rounded-[12px] border border-solid border-warning-line bg-warning-tint px-3 py-2.5 text-footnote text-warning-ink">
                                <Info size={16} className="mt-0.5 shrink-0 !p-0"/>
                                <span>
                                    Choose Public or Shared before saving. {privateRestriction.reason}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex h-16 shrink-0 items-center justify-end gap-2 border-0 border-t border-solid border-line bg-surface px-5">
                    <PhoneHQButton variant="secondary" onClick={handleClose}>Cancel</PhoneHQButton>
                    <PhoneHQButton
                        variant="primary"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || (isEdit && !contactData) || privateAvailabilityNeedsChanging}
                    >
                        {submitLabel}
                    </PhoneHQButton>
                </div>
            </div>
        </div>
    );
}
